// src/utils/inputSanitizer.ts
import { useState } from 'react';

/**
 * 入力値のセキュリティ対策を一元管理するユーティリティ
 * XSSやSQLインジェクション、その他の一般的な脆弱性に対する防御機能を提供
 */

/**
 * 入力値に対するサニタイズオプション
 */
export interface SanitizeOptions {
  /** HTMLタグを削除するかどうか */
  stripHtml?: boolean;
  /** スクリプトタグを削除するかどうか */
  stripScripts?: boolean;
  /** SQLインジェクション対策を適用するかどうか */
  preventSqlInjection?: boolean;
  /** 長さ制限（最大文字数） */
  maxLength?: number;
  /** URL形式のみを許可（URLフィールド用） */
  urlOnly?: boolean;
  /** 英数字とアンダースコアのみを許可（ユーザー名など） */
  alphanumericOnly?: boolean;
  /** 小文字に変換するかどうか */
  toLowerCase?: boolean;
}

/**
 * デフォルトのサニタイズオプション
 */
const defaultOptions: SanitizeOptions = {
  stripHtml: true,
  stripScripts: true,
  preventSqlInjection: true,
  maxLength: undefined,
  urlOnly: false,
  alphanumericOnly: false,
  toLowerCase: false
};

/**
 * 入力値のサニタイズを行う関数
 * @param input サニタイズする入力値
 * @param options サニタイズオプション
 * @returns サニタイズされた文字列
 */
export function sanitizeInput(input: string, options: SanitizeOptions = {}): string {
  // デフォルトオプションと結合
  const opts = { ...defaultOptions, ...options };

  if (!input) return input;

  let sanitized = input;

  // 小文字変換（ユーザー名用）
  if (opts.toLowerCase) {
    sanitized = sanitized.toLowerCase();
  }

  // HTMLタグを削除
  if (opts.stripHtml) {
    sanitized = sanitized.replace(/<[^>]*>/g, '');
  }

  // スクリプトタグと潜在的な危険な属性を削除
  if (opts.stripScripts) {
    // スクリプトタグを削除
    sanitized = sanitized.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
    // onclick、onerrorなどのイベントハンドラ属性を削除
    sanitized = sanitized.replace(/on\w+="[^"]*"/g, '');
    sanitized = sanitized.replace(/on\w+='[^']*'/g, '');
    // javascript:プロトコルを削除
    sanitized = sanitized.replace(/javascript:/gi, '');
    // data:プロトコルを削除（データURLによるXSS対策）
    sanitized = sanitized.replace(/data:/gi, '');
  }

  // SQLインジェクション対策
  if (opts.preventSqlInjection) {
    // SQLコマンドのパターンを検出して削除または置換
    sanitized = sanitized.replace(/(\b)(SELECT|INSERT|UPDATE|DELETE|DROP|ALTER|EXEC|UNION|CREATE|WHERE)(\b)/gi, '$1**$3');
  }

  // URL形式のみを許可（セキュリティ強化版）
  if (opts.urlOnly && sanitized.length > 0) {
    // 危険なプロトコルを先にチェック
    if (/^(javascript|data|vbscript|file|about|blob):/i.test(sanitized)) {
      return '';
    }

    // 完全なURLかプロトコルなしのドメイン名かを確認
    const hasProtocol = /^https?:\/\//i.test(sanitized);

    if (hasProtocol) {
      try {
        const url = new URL(sanitized);
        // 安全なプロトコルのみ許可（httpとhttpsのみ）
        if (!['http:', 'https:'].includes(url.protocol)) {
          return '';
        }
      } catch {
        return '';
      }
    } else if (sanitized.trim()) {
      // プロトコルなしの場合、httpsを追加して検証
      try {
        const testUrl = new URL(`https://${sanitized}`);
        // 基本的なドメイン構造の検証
        if (!/^[a-zA-Z0-9][-a-zA-Z0-9.]*\.[a-zA-Z]{2,}/.test(testUrl.hostname)) {
          return '';
        }
      } catch {
        return '';
      }
    }
  }

  // 英数字とアンダースコアのみを許可
  if (opts.alphanumericOnly) {
    sanitized = sanitized.replace(/[^a-zA-Z0-9_]/g, '');
  }

  // 文字数制限（最後に適用）
  if (opts.maxLength !== undefined && sanitized.length > opts.maxLength) {
    sanitized = sanitized.slice(0, opts.maxLength);
  }

  return sanitized;
}

/**
 * 表示名用のサニタイズオプションのプリセット
 */
export const displayNameSanitizeOptions: SanitizeOptions = {
  stripHtml: true,
  stripScripts: true,
  preventSqlInjection: false, // FirestoreはNoSQLのためSQL対策不要。'や"の通常使用を許可
  maxLength: 25
};

/**
 * 自己紹介用のサニタイズオプションのプリセット
 */
export const bioSanitizeOptions: SanitizeOptions = {
  stripHtml: true,
  stripScripts: true,
  preventSqlInjection: false, // FirestoreはNoSQLのためSQL対策不要。'や"の通常使用を許可
  maxLength: 150
};

/**
 * ユーザー名用のサニタイズオプションのプリセット
 */
export const usernameSanitizeOptions: SanitizeOptions = {
  stripHtml: true,
  stripScripts: true,
  preventSqlInjection: true,
  alphanumericOnly: true,
  toLowerCase: true,
  maxLength: 20
};

/**
 * サニタイザーのカスタムフック（React用）
 */
export function useSanitizedInput(initialValue: string = '', options: SanitizeOptions = {}) {
  const [value, setValue] = useState(sanitizeInput(initialValue, options));

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setValue(sanitizeInput(e.target.value, options));
  };

  return {
    value,
    onChange: handleChange,
    setValue: (newValue: string) => setValue(sanitizeInput(newValue, options))
  };
}
